import { emitEffect } from '../../effects/emitter';
import type { CopilotEffect, EffectPayloads } from '../../effects/types';

import styles from './MessageArtifacts.module.css';

type PlotData = EffectPayloads['SHOW_MEASUREMENT_PLOT'];

const W = 280;
const H = 130;
const PAD = { top: 12, right: 12, bottom: 18, left: 34 };

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function markerColor(inRange: boolean | null): string {
  if (inRange === false) return '#ff6b6b';
  if (inRange === true) return '#51cf66';
  return '#adb5bd';
}

function caption(data: PlotData): string {
  const s = data.summary;
  const parts: string[] = [];
  if (s?.trend) parts.push(s.trend);
  if (s?.delta_pct !== null && s?.delta_pct !== undefined) {
    parts.push(`${s.delta_pct > 0 ? '+' : ''}${s.delta_pct}% vs first`);
  }
  if (s?.out_of_range_count) parts.push(`${s.out_of_range_count} out of range`);
  return parts.join(' · ');
}

/** AP-450: inline time-series of a measurement, dependency-free SVG. Markers
 * click through to their study via NAVIGATE_TO_STUDY. */
export function MeasurementPlot({ data }: { data: PlotData }) {
  const points = data.series ?? [];
  const label = data.abbreviation || data.name;

  if (points.length === 0) {
    return (
      <div className={styles.artifact}>
        <div className={styles.title}>{data.name}</div>
        <div className={styles.empty}>No measurement values to plot.</div>
      </div>
    );
  }

  const lo = data.reference_range?.low ?? null;
  const hi = data.reference_range?.high ?? null;
  const domain = [
    ...points.map((p) => p.value),
    ...(lo !== null ? [lo] : []),
    ...(hi !== null ? [hi] : []),
  ];
  let min = Math.min(...domain);
  let max = Math.max(...domain);
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const pad = (max - min) * 0.1;
  min -= pad;
  max += pad;

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (i: number) =>
    points.length === 1
      ? PAD.left + plotW / 2
      : PAD.left + (i / (points.length - 1)) * plotW;
  const y = (v: number) => PAD.top + (1 - (v - min) / (max - min)) * plotH;

  const linePath = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`
    )
    .join(' ');

  return (
    <div className={styles.artifact}>
      <div className={styles.title}>
        {label} <span className={styles.unit}>({data.unit})</span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.svg}
        role="img"
        aria-label={`${data.name} across ${points.length} studies`}
      >
        {lo !== null && hi !== null && (
          <rect
            x={PAD.left}
            y={y(hi)}
            width={plotW}
            height={Math.max(0, y(lo) - y(hi))}
            className={styles.band}
          />
        )}
        <line
          x1={PAD.left}
          y1={PAD.top}
          x2={PAD.left}
          y2={PAD.top + plotH}
          className={styles.axis}
        />
        {points.length > 1 && <path d={linePath} className={styles.line} />}
        {points.map((p, i) => (
          <circle
            key={`${p.study_uuid}-${p.value}`}
            cx={x(i)}
            cy={y(p.value)}
            r={4}
            fill={markerColor(p.in_range)}
            className={styles.marker}
            onClick={() =>
              emitEffect({
                type: 'NAVIGATE_TO_STUDY',
                payload: { study_uuid: p.study_uuid },
              } as CopilotEffect)
            }
          >
            <title>
              {`${p.accession || 'study'} · ${fmt(p.value)} ${p.unit}${
                p.study_date ? ` · ${p.study_date}` : ''
              }`}
            </title>
          </circle>
        ))}
        <text
          x={PAD.left - 4}
          y={y(max) + 3}
          className={styles.axisLabel}
          textAnchor="end"
        >
          {fmt(max)}
        </text>
        <text
          x={PAD.left - 4}
          y={y(min)}
          className={styles.axisLabel}
          textAnchor="end"
        >
          {fmt(min)}
        </text>
      </svg>
      {caption(data) && <div className={styles.caption}>{caption(data)}</div>}
    </div>
  );
}
